import { Link } from 'react-router-dom';
import type { GossipEvent } from '@/types';
import { formatHeat, trendClass, trendLabel } from '@/utils/format';

interface EventCardProps {
  event: GossipEvent;
  variant?: 'default' | 'feature' | 'row';
}

export default function EventCard({ event, variant = 'default' }: EventCardProps) {
  if (variant === 'row') {
    return (
      <Link
        to={`/timeline/${event.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
      >
        <div className="relative h-24 w-28 shrink-0 overflow-hidden rounded-md md:h-28 md:w-36">
          <img
            src={event.cover}
            alt={`${event.title} - 蝌蚪吃瓜时间线封面`}
            title={`${event.title} - 蝌蚪吃瓜时间线`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-background-100 px-2 py-0.5 font-label text-xs text-foreground-600">
              {event.categoryName}
            </span>
            <span className={`rounded-full px-2 py-0.5 font-label text-xs ${trendClass(event.trend)}`}>
              {trendLabel(event.trend)}
            </span>
          </div>
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
            {event.title}
          </h3>
          <p className="mt-2 hidden text-sm leading-relaxed text-foreground-600 sm:block line-clamp-2">
            {event.summary}
          </p>
          <div className="mt-auto flex flex-wrap items-center gap-3 pt-2 font-label text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" />
              {formatHeat(event.heat)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {event.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {event.date}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  const isFeature = variant === 'feature';
  return (
    <Link
      to={`/timeline/${event.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className={`relative w-full overflow-hidden ${isFeature ? 'h-56 md:h-72' : 'h-44 md:h-52'}`}>
        <img
          src={event.cover}
          alt={`${event.title} - 蝌蚪吃瓜事件时间线`}
          title={`${event.title} - 蝌蚪吃瓜`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-foreground-950/70 px-2.5 py-1 font-label text-xs text-background-50">
          {event.categoryName}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3
          className={`font-heading font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 ${
            isFeature ? 'text-xl md:text-2xl' : 'text-base md:text-lg'
          }`}
        >
          {event.title}
        </h3>
        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">{event.summary}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {event.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-full bg-secondary-50 px-2 py-0.5 font-label text-xs text-secondary-800">
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3 font-label text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" />
            {formatHeat(event.heat)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-git-commit-line" />
            {event.timeline.length} 个节点
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {event.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}